import { evaluateExpression } from "@/lib/mapper/expression";
import { lookupMapping } from "@/lib/mapper/mappers";
import { substituteExpression } from "@/modules/_shared/templateValue";
import type { CellModule } from "@/modules/_shared/contract";
import type { ResolveCtx } from "@/modules/_shared/engine";

/** Function table exposed to the Expression module. Pure, total. */
function expressionFns(ctx: ResolveCtx): Record<string, (...a: unknown[]) => unknown> {
  const num = (v: unknown) => {
    const n = Number(String(v).replace(/[^0-9.\-]/g, ""));
    return Number.isFinite(n) ? n : 0;
  };
  return {
    map: (v: unknown, slug: unknown) => {
      const hit = lookupMapping(String(v), ctx.dictionaries?.[String(slug)]);
      return hit ?? String(v);
    },
    round: (v: unknown) => Math.round(num(v)),
    floor: (v: unknown) => Math.floor(num(v)),
    ceil: (v: unknown) => Math.ceil(num(v)),
    number: (v: unknown) => num(v),
    len: (v: unknown) => String(v).length,
    lower: (v: unknown) => String(v).toLowerCase(),
    upper: (v: unknown) => String(v).toUpperCase(),
    trim: (v: unknown) => String(v).trim(),
    concat: (...a: unknown[]) => a.map(String).join(""),
    replace: (s: unknown, from: unknown, to: unknown) => String(s).split(String(from)).join(String(to)),
    padStart: (s: unknown, len: unknown, pad: unknown) => String(s).padStart(num(len), String(pad ?? "0")),
    default: (s: unknown, fb: unknown) => (String(s).trim() === "" ? String(fb) : String(s)),
  };
}

export const expressionModule: CellModule = {
  kind: "expression",
  capability: "cell",
  name: "Calculate",
  tooltip: "Do math, like price + 500",
  description:
    "Works out a number using a simple formula, like adding 500 to a price. Type / in the editor to load a column.",
  category: "Rules",
  tags: ["formula", "math", "calc", "compute", "price", "arithmetic", "+", "expression"],
  icon: { kind: "lucide", name: "Calculator" },
  tone: "amber",
  defaultConfig: () => ({ kind: "expression", expr: "" }),
  resolve: (rule, ctx) => {
    if (rule.kind !== "expression") return "";
    const substituted = substituteExpression(rule.expr, ctx);
    return evaluateExpression(substituted, { fns: expressionFns(ctx) });
  },
  templateStrings: (rule) => (rule.kind === "expression" ? [rule.expr] : []),
};
